/**
 * Opus API client — thin promise-based wrapper over the native `fetch`.
 *
 * Spec requires native fetch (no axios). This file is the SINGLE place that
 * touches `fetch` directly; every page/store imports the typed helpers below
 * so we can later add: retry, auth refresh, request cancellation, telemetry,
 * CSRF tokens — without touching call sites.
 *
 * Conventions:
 *   - All non-2xx responses throw `ApiError` (so call sites can `.catch`).
 *   - JSON in / JSON out by default. `body` is auto-stringified if it's an object.
 *   - The base URL comes from `VITE_API_BASE_URL` so we can point at:
 *       - localhost during dev (Django dev server)
 *       - the Opus production URL once deployed
 *     Without rebuilding, by overriding the env var at container start time.
 *   - CSRF: Django expects an `X-CSRFToken` header on unsafe verbs; we read
 *     it from the `csrftoken` cookie automatically. Safe verbs skip it.
 */

import { routeCachePolicyFor, shouldBypassRequestCache } from "@/api/cache-policy";
import { getLogger } from "@/lib/logging";

const logger = getLogger("opus.api");

/* ---------- Types ---------- */

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  /** HTTP verb. Default GET. */
  method?: HttpMethod;
  /** Body — objects are JSON-stringified, FormData/Blob are passed through. */
  body?: unknown;
  /** Extra headers to merge over the defaults. */
  headers?: Record<string, string>;
  /** Query string parameters; appended to the URL. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** AbortSignal for cancellation (e.g. on unmount). */
  signal?: AbortSignal;
  /** If true, the response is returned as text instead of JSON. */
  asText?: boolean;
  /** Per-call cache override for GET requests. */
  cacheMode?: "default" | "bypass";
}

/**
 * Thrown by every non-2xx response. `data` is the parsed body if available,
 * so call sites can do `if (err instanceof ApiError && err.status === 401) {...}`.
 */
export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/* ---------- Internal helpers ---------- */

/** Read a cookie value by name. Used to grab Django's csrftoken cookie. */
function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(^|;\\s*)(" + name + ")=([^;]+)"),
  );
  return match ? decodeURIComponent(match[3]) : null;
}

/** Append `?a=1&b=2` to a path, dropping nullish values. */
function buildUrl(
  base: string,
  path: string,
  query?: RequestOptions["query"],
): string {
  // Compose the final path. Callers may pass "/foo" or "foo"; the base may be
  // absolute ("https://api.example.com") or relative ("/api"). The URL
  // constructor refuses relative URLs without a base, so we fall back to
  // window.location.origin when the composed string is still relative.
  const composed = path.startsWith("http")
    ? path
    : `${base.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
  const url = composed.startsWith("http")
    ? new URL(composed)
    : new URL(composed, typeof window !== "undefined" ? window.location.origin : "http://localhost");
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

/* ---------- Public API ---------- */

export const API_BASE: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

const REQUEST_CACHE_STORAGE_PREFIX = "opus:api:get:";
type RequestCacheEntry = { expiresAt: number; payload: unknown; };
const inMemoryRequestCache = new Map<string, RequestCacheEntry>();

/**
 * In-flight request deduplication map.
 *
 * When two callers request the same cacheable GET before the first response
 * returns (e.g. the 30 s auto-refresh fires the same instant a user clicks
 * Refresh), both callers get the same Promise instead of two network calls.
 * The entry is removed as soon as the request settles (success or error).
 */
const inFlightRequests = new Map<string, Promise<unknown>>();

function nowMs(): number {
  return Date.now();
}

function getRequestCacheKey(method: HttpMethod, url: string): string {
  return `${method}:${url}`;
}

function getStorageKey(cacheKey: string): string {
  return `${REQUEST_CACHE_STORAGE_PREFIX}${cacheKey}`;
}

function readSessionCache(cacheKey: string): RequestCacheEntry | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(getStorageKey(cacheKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RequestCacheEntry;
    if (!parsed || typeof parsed.expiresAt !== "number") {
      window.sessionStorage.removeItem(getStorageKey(cacheKey));
      return null;
    }
    if (parsed.expiresAt <= nowMs()) {
      window.sessionStorage.removeItem(getStorageKey(cacheKey));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionCache(cacheKey: string, entry: RequestCacheEntry): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(getStorageKey(cacheKey), JSON.stringify(entry));
  } catch {
    // Ignore quota/storage errors and keep memory cache as fallback for this tab.
  }
}

function getCachedResponse(cacheKey: string): unknown | undefined {
  const mem = inMemoryRequestCache.get(cacheKey);
  if (mem) {
    if (mem.expiresAt > nowMs()) return mem.payload;
    inMemoryRequestCache.delete(cacheKey);
  }

  const persisted = readSessionCache(cacheKey);
  if (!persisted) return undefined;
  inMemoryRequestCache.set(cacheKey, persisted);
  return persisted.payload;
}

function setCachedResponse(cacheKey: string, payload: unknown, ttlMs: number): void {
  if (ttlMs <= 0) return;
  const entry: RequestCacheEntry = { expiresAt: nowMs() + ttlMs, payload };
  inMemoryRequestCache.set(cacheKey, entry);
  writeSessionCache(cacheKey, entry);
}

export function clearRequestCache(): void {
  inMemoryRequestCache.clear();
  if (typeof window === "undefined") return;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key?.startsWith(REQUEST_CACHE_STORAGE_PREFIX)) keysToRemove.push(key);
    }
    for (const key of keysToRemove) window.sessionStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures.
  }
}

/**
 * Core request helper. Most code should use the verb shortcuts (`get`, `post`, …)
 * below rather than calling this directly.
 */
export async function request<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const method = opts.method ?? "GET";
  const isUnsafe = method !== "GET";
  const url = buildUrl(API_BASE, path, opts.query);
  const cachePolicy = routeCachePolicyFor(method, url);
  const bypassCache = opts.cacheMode === "bypass" || shouldBypassRequestCache(opts.query);
  const cacheKey = getRequestCacheKey(method, url);
  const startTime = performance.now();

  if (cachePolicy.enabled && !bypassCache) {
    const cached = getCachedResponse(cacheKey);
    if (cached !== undefined) {
      const elapsed = performance.now() - startTime;
      logger.debug({ method, path, elapsed_ms: Math.round(elapsed) }, "api_cache_hit");
      return cached as T;
    }

    // Return the in-flight promise if the same request is already underway.
    const inflight = inFlightRequests.get(cacheKey);
    if (inflight) {
      logger.debug({ method, path }, "api_inflight_dedup");
      return inflight as Promise<T>;
    }
  }

  // Default headers — JSON in/out. Caller can override.
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.body && !(opts.body instanceof FormData) && !(opts.body instanceof Blob)
      ? { "Content-Type": "application/json" }
      : {}),
    ...opts.headers,
  };

  // Django CSRF: only attach on unsafe verbs.
  if (isUnsafe) {
    const csrf = getCookie("csrftoken");
    if (csrf) headers["X-CSRFToken"] = csrf;
  }

  // Body normalization — JSON-stringify objects, pass through binary.
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (opts.body instanceof FormData || opts.body instanceof Blob) {
      body = opts.body;
    } else {
      body = JSON.stringify(opts.body);
    }
  }

  logger.debug({ method, path }, "api_request_start");

  const fetchPromise = (async () => {
    const res = await fetch(url, {
      method,
      headers,
      body,
      credentials: "include", // Session cookies for Django auth.
      signal: opts.signal,
    });

    const contentType = res.headers.get("content-type")?.toLowerCase() ?? "";
    const isJson = contentType.includes("application/json");

    if (!res.ok) {
      // NOTE: an earlier version dispatched a global 'opus-auth-expired'
      // event on every 401 to bounce the user to /login. That backfired
      // when cookies weren't sent at all (e.g. Secure cookie over plain
      // HTTP) — every call 401'd, every 401 forced a redirect, and the
      // user got stuck in an unbreakable loop after a "successful" login.
      // Per-call sites that want the bounce can listen for ApiError.status
      // === 401 themselves; we no longer force it globally.
      const errorParsed: unknown = opts.asText
        ? await res.text()
        : isJson && res.status !== 204
        ? await res.json()
        : await res.text();
      const elapsed = performance.now() - startTime;
      const errorPreview = typeof errorParsed === "object" ? JSON.stringify(errorParsed).slice(0, 200) : String(errorParsed).slice(0, 200);
      logger.warn({ method, path, status_code: res.status, elapsed_ms: Math.round(elapsed), error_preview: errorPreview }, "api_request_error");
      throw new ApiError(
        `HTTP ${res.status} on ${method} ${path}`,
        res.status,
        errorParsed,
      );
    }

    let parsed: unknown;
    if (opts.asText) {
      parsed = await res.text();
    } else if (isJson) {
      // 204 No Content has no body — guard against res.json() throwing.
      parsed = res.status === 204 ? null : await res.json();
    } else {
      parsed = await res.text();
    }

    const elapsed = performance.now() - startTime;

    if (isUnsafe) {
      clearRequestCache();
    }

    // Defense: when the caller expects JSON (the default), refuse to return
    // HTML masquerading as a 200. This protects against gateway misconfigs
    // where /api/* falls through to a SPA shell — without this guard, the
    // string body would be silently returned and crash downstream consumers.
    if (!opts.asText && !isJson && parsed !== null && parsed !== "") {
      logger.warn({ method, path, expected: "json", got: contentType, elapsed_ms: Math.round(elapsed) }, "api_request_content_type_mismatch");
      throw new ApiError(
        `Expected JSON from ${method} ${path}, got ${contentType || "unknown"}`,
        res.status,
        parsed,
      );
    }

    if (cachePolicy.enabled && !bypassCache) {
      logger.debug({ method, path, ttl_ms: cachePolicy.ttlMs }, "api_cache_write");
      setCachedResponse(cacheKey, parsed, cachePolicy.ttlMs);
    }

    logger.debug({ method, path, status_code: res.status, elapsed_ms: Math.round(elapsed) }, "api_request_success");

    return parsed;
  })();

  // Register in-flight so concurrent callers can share this promise.
  if (cachePolicy.enabled && !bypassCache) {
    inFlightRequests.set(cacheKey, fetchPromise);
    void fetchPromise.finally(() => inFlightRequests.delete(cacheKey)).catch(() => {});
  }

  return fetchPromise as Promise<T>;
}

/** GET shortcut. */
export const get = <T = unknown>(
  path: string,
  query?: RequestOptions["query"],
  signal?: AbortSignal,
) => request<T>(path, { method: "GET", query, signal });

/** POST shortcut. */
export const post = <T = unknown>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
) => request<T>(path, { method: "POST", body, signal });

/** PUT shortcut. */
export const put = <T = unknown>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
) => request<T>(path, { method: "PUT", body, signal });

/** PATCH shortcut. */
export const patch = <T = unknown>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
) => request<T>(path, { method: "PATCH", body, signal });

/** DELETE shortcut. */
export const del = <T = unknown>(path: string, signal?: AbortSignal) =>
  request<T>(path, { method: "DELETE", signal });

/**
 * Authenticated file download. Fetches `path` with session credentials,
 * then triggers a browser save-as for the response body under `filename`.
 */
export async function downloadBlob(path: string, filename: string): Promise<void> {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new ApiError(`Download failed: ${res.status}`, res.status, null);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
